// client/components/widgets/registry.js
// ✅ Widget-Registry: Definiert alle verfügbaren Dashboard-Widgets
import { lazy } from "react";
import {
  CloudSun,
  ClipboardCheck,
  ListTodo,
  CalendarDays,
  Zap,
  LayoutDashboard,
  Users,
  Cake,
  Scale,
  StickyNote,
  Bitcoin,
  Radio,
  Shield,
  Gamepad2,
  GraduationCap,
} from "lucide-react";

// Rollen-IDs (müssen mit Backend übereinstimmen)
const ROLE_ADMIN = 1;
const ROLE_GL = 2;
const ROLE_PL = 3;
const ROLE_SEK = 4;
const ROLE_MITARBEITER = 5;
const ROLE_LEHRLING = 6;

const ALL_ROLES = [ROLE_ADMIN, ROLE_GL, ROLE_PL, ROLE_SEK, ROLE_MITARBEITER, ROLE_LEHRLING];
const POWER_ROLES = [ROLE_ADMIN, ROLE_GL, ROLE_PL, ROLE_SEK];

// Lazy-Load Widget-Komponenten
const DateWeatherWidget = lazy(() => import("./items/DateWeatherWidget.jsx"));
const MyPendenzenWidget = lazy(() => import("./items/MyPendenzenWidget.jsx"));
const MyTasksWidget = lazy(() => import("./items/MyTasksWidget.jsx"));
const TermineTodayWidget = lazy(() => import("./items/TermineTodayWidget.jsx"));
const QuickActionsWidget = lazy(() => import("./items/QuickActionsWidget.jsx"));
const QuickNotesWidget = lazy(() => import("./items/QuickNotesWidget.jsx"));
const LeitstandMiniWidget = lazy(() => import("./items/LeitstandMiniWidget.jsx"));
const ManagementIntelligenceWidget = lazy(() => import("./items/ManagementIntelligenceWidget.jsx"));
const YearBalanceWidget = lazy(() => import("./items/YearBalanceWidget.jsx"));
const AbsencesTodayWidget = lazy(() => import("./items/AbsencesTodayWidget.jsx"));
const BirthdaysWidget = lazy(() => import("./items/BirthdaysWidget.jsx"));
const BitcoinWidget = lazy(() => import("./items/BitcoinWidget.jsx"));
const RadioWidget = lazy(() => import("./items/RadioWidget.jsx"));
const ArcadeWidget = lazy(() => import("./items/ArcadeWidget.jsx"));
const ServiceFalltypWidget = lazy(() => import("./items/ServiceFalltypWidget.jsx"));
const AcademyWidget = lazy(() => import("./items/AcademyWidget.jsx"));

/**
 * Widget-Registry - Alle verfügbaren Widgets
 */
export const WIDGET_REGISTRY = {
  // ═══════════════════════════════════════════
  // Info Widgets
  // ═══════════════════════════════════════════

  "date-weather": {
    id: "date-weather",
    name: "Datum & Wetter",
    description: "Uhrzeit, Datum, Kalenderwoche und aktuelles Wetter",
    icon: CloudSun,
    component: DateWeatherWidget,
    allowedRoles: ALL_ROLES,
    refreshInterval: 30 * 60 * 1000, // 30 Min für Wetter
    category: "info",
  },

  "termine-today": {
    id: "termine-today",
    name: "Termine",
    description: "Termine heute und morgen",
    icon: CalendarDays,
    component: TermineTodayWidget,
    allowedRoles: ALL_ROLES,
    refreshInterval: 5 * 60 * 1000,
    category: "tasks",
  },

  // ═══════════════════════════════════════════
  // Persönliche Widgets
  // ═══════════════════════════════════════════

  "my-pendenzen": {
    id: "my-pendenzen",
    name: "Meine Pendenzen",
    description: "Deine offenen und überfälligen Pendenzen",
    icon: ClipboardCheck,
    component: MyPendenzenWidget,
    allowedRoles: ALL_ROLES,
    refreshInterval: 60 * 1000,
    category: "tasks",
  },

  "my-tasks": {
    id: "my-tasks",
    name: "Meine Aufgaben",
    description: "Deine nächsten zugewiesenen Aufgaben",
    icon: ListTodo,
    component: MyTasksWidget,
    allowedRoles: ALL_ROLES,
    refreshInterval: 2 * 60 * 1000,
    category: "tasks",
  },

  "year-balance": {
    id: "year-balance",
    name: "Jahressaldo",
    description: "Ferien, Überzeit und Kompensation",
    icon: Scale,
    component: YearBalanceWidget,
    allowedRoles: ALL_ROLES,
    refreshInterval: 24 * 60 * 60 * 1000,
    category: "personal",
  },

  "academy-progress": {
    id: "academy-progress",
    name: "Academy",
    description: "Pflicht-Onboarding: Fortschritt und nächster Schritt",
    icon: GraduationCap,
    component: AcademyWidget,
    allowedRoles: ALL_ROLES,
    refreshInterval: 60 * 1000,
    category: "personal",
    /** Während needAcademy nicht entfernbar (UI) */
    mandatoryWhileAcademy: true,
  },

  // ═══════════════════════════════════════════
  // Aktionen
  // ═══════════════════════════════════════════

  "quick-actions": {
    id: "quick-actions",
    name: "Quick Actions",
    description: "Schnellzugriff auf häufige Aktionen",
    icon: Zap,
    component: QuickActionsWidget,
    allowedRoles: POWER_ROLES,
    refreshInterval: null,
    category: "actions",
  },

  "quick-notes": {
    id: "quick-notes",
    name: "Quick Notes",
    description: "Persönliche Schnellnotizen",
    icon: StickyNote,
    component: QuickNotesWidget,
    allowedRoles: ALL_ROLES,
    refreshInterval: null,
    category: "actions",
  },

  // ═══════════════════════════════════════════
  // Business Widgets (Power Roles)
  // ═══════════════════════════════════════════

  "leitstand-mini": {
    id: "leitstand-mini",
    name: "Leitstand",
    description: "Übersicht aller aktiven Projekte",
    icon: LayoutDashboard,
    component: LeitstandMiniWidget,
    allowedRoles: POWER_ROLES,
    refreshInterval: 5 * 60 * 1000,
    category: "business",
  },

  "management-intelligence": {
    id: "management-intelligence",
    name: "Management KPIs",
    description: "Projekte, Stunden, Material und A-Konto auf einen Blick",
    icon: LayoutDashboard,
    component: ManagementIntelligenceWidget,
    allowedRoles: POWER_ROLES,
    refreshInterval: 5 * 60 * 1000,
    category: "business",
  },

  "service-falltyp": {
    id: "service-falltyp",
    name: "Service Falltypen",
    description: "Verteilung Garantie / Kulanz / Normal",
    icon: Shield,
    component: ServiceFalltypWidget,
    allowedRoles: POWER_ROLES,
    refreshInterval: 5 * 60 * 1000,
    category: "business",
  },

  "absences-today": {
    id: "absences-today",
    name: "Abwesenheiten",
    description: "Wer ist heute nicht da",
    icon: Users,
    component: AbsencesTodayWidget,
    allowedRoles: POWER_ROLES,
    refreshInterval: 24 * 60 * 60 * 1000,
    category: "team",
  },

  birthdays: {
    id: "birthdays",
    name: "Geburtstage",
    description: "Anstehende Geburtstage im Team",
    icon: Cake,
    component: BirthdaysWidget,
    allowedRoles: ALL_ROLES,
    refreshInterval: 24 * 60 * 60 * 1000,
    category: "team",
  },

  // ═══════════════════════════════════════════
  // Fun Widgets
  // ═══════════════════════════════════════════

  bitcoin: {
    id: "bitcoin",
    name: "Crypto",
    description: "Bitcoin & Ethereum Kurse",
    icon: Bitcoin,
    component: BitcoinWidget,
    allowedRoles: ALL_ROLES,
    refreshInterval: 5 * 60 * 1000,
    category: "fun",
  },

  radio: {
    id: "radio",
    name: "Radio",
    description: "Schweizer Radio-Sender streamen",
    icon: Radio,
    component: RadioWidget,
    allowedRoles: ALL_ROLES,
    refreshInterval: null, // Kein Auto-Refresh nötig
    category: "fun",
  },

  arcade: {
    id: "arcade",
    name: "Arcade",
    description: "Snake, Pong, Breakout & 2048",
    icon: Gamepad2,
    component: ArcadeWidget,
    allowedRoles: ALL_ROLES,
    refreshInterval: null,
    category: "fun",
  },
};

/**
 * Widget-Kategorien für Gruppierung im Modal
 */
export const WIDGET_CATEGORIES = {
  info: { id: "info", name: "Information", description: "Zeit, Wetter, Kalender" },
  personal: { id: "personal", name: "Persönlich", description: "Deine Statistiken" },
  tasks: { id: "tasks", name: "Aufgaben", description: "Pendenzen und Aufgaben" },
  actions: { id: "actions", name: "Aktionen", description: "Schnellzugriffe" },
  team: { id: "team", name: "Team", description: "Team-Infos" },
  business: { id: "business", name: "Business", description: "Projekte und Geschäft" },
  fun: { id: "fun", name: "Fun", description: "Unterhaltung & Extras" },
};

const WIDGET_ID_ALIASES = { snake: "arcade" };

/**
 * Entfernte Widget-IDs — Stunden-KPIs leben in DashboardTopBarKpis (overview.topmini).
 * Gespeicherte Layouts mit diesen IDs werden still ignoriert.
 */
export const REMOVED_WIDGET_IDS = new Set(["hours-status"]);

/**
 * Helper: Widget nach ID holen
 */
export function getWidgetById(id) {
  const resolved = WIDGET_ID_ALIASES[id] || id;
  if (REMOVED_WIDGET_IDS.has(resolved)) return null;
  return WIDGET_REGISTRY[resolved] || null;
}

/**
 * Helper: Alle Widgets für eine Rolle filtern
 */
export function getWidgetsForRole(roleId) {
  return Object.values(WIDGET_REGISTRY).filter((widget) =>
    widget.allowedRoles.includes(Number(roleId))
  );
}

export default WIDGET_REGISTRY;
